import React from 'react';
import {
  Dialog,
  Box,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Avatar,
  CircularProgress,
  Button,
  Tooltip,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import PersonIcon from '@mui/icons-material/Person';
import PrintIcon from '@mui/icons-material/Print';

import CustomerSummary from './CustomerSummary.js';
import CarList from './CarList.js';
import ServiceList from './ServiceList.js';
import CreditAppsTab from './CreditAppsTab.js';
import MergeCustomersDialog from '../../components/MergeCustomersDialog.js';
import SavedCardsList from '../../components/SavedCardsList.js';
import EntityFiles from '../../components/EntityFiles.js';

import firebase from '../../utilities/firebase';
import history from '../../utilities/history';
import { StateManager } from '../../utilities/stateManager';
import { useAutoZ, ModalStackProvider } from '../../utilities/ModalStackContext';
import { useTallDialogSizing } from '../../utilities/dialogSizing';

const VISIBLE_TABS = ['summary', 'cars', 'service', 'credit-apps', 'payment-methods', 'files'];

const formatTitle = raw => {
  const spaced = raw.split('-').join(' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const Loading = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
    <CircularProgress />
  </Box>
);

export default function CustomerModal({ open, customerId, onClose }) {
  const myZ = useAutoZ(open, customerId);
  const { fullScreen, paperSx } = useTallDialogSizing({ height: '95vh', margin: 1.5, radius: 2 });
  const theme = useTheme();
  const id = customerId;

  const [loading, setLoading] = React.useState(true);
  const [customerData, setCustomerData] = React.useState({});
  const [carData, setCarData] = React.useState({});
  const [serviceData, setServiceData] = React.useState({});
  const [profileImageUrl, setProfileImageUrl] = React.useState(null);
  const [refreshTrigger, setRefreshTrigger] = React.useState(0);
  const [activeTab, setActiveTab] = React.useState(0);
  const [mergeDialogOpen, setMergeDialogOpen] = React.useState(false);
  const tabContentRef = React.useRef(null);

  // Reset + load data when customerId changes
  React.useEffect(() => {
    setCustomerData({});
    setCarData({});
    setServiceData({});
    setProfileImageUrl(null);

    if (!id) {
      setLoading(false);
      return;
    }

    // Read initial tab from URL
    const tabParam = new URL(window.location.href).searchParams.get('customerTab');
    const idx = VISIBLE_TABS.indexOf(tabParam);
    setActiveTab(idx >= 0 ? idx : 0);

    let cancelled = false;
    setLoading(true);

    async function fetchAllData() {
      const db = firebase.firestore();

      try {
        const customerDoc = await db.doc('customers/' + id).get();
        if (cancelled) return;

        if (customerDoc.exists) {
          const customer = { id: customerDoc.id, ...customerDoc.data() };
          setCustomerData(customer);

          if (customer.license) {
            try {
              const url = await firebase.app().storage("skyway-dev-373d5.appspot.com").ref(customer.license).getDownloadURL();
              if (!cancelled) setProfileImageUrl(url);
            } catch (error) {
              if (!cancelled) setProfileImageUrl('/missing_image.jpeg');
            }
          } else {
            setProfileImageUrl('/missing_image.jpeg');
          }
        }

        const customer = customerDoc.exists ? customerDoc.data() : {};
        const carQueries = [
          { collection: 'deals', doc: 'buyer' },
          { collection: 'deals', doc: 'cobuyer' },
          { collection: 'cars', doc: 'consignor' },
          { collection: 'leads', doc: 'email', value: customer.email },
          { collection: 'leads', doc: 'phone', value: customer.phone },
        ];

        const carQueryPromises = carQueries.map(({ collection, doc, value }) =>
          db.collection(collection).where(doc, '==', value || id).get()
        );

        const carQueryResults = await Promise.all(carQueryPromises);
        if (cancelled) return;

        const carDocs = carQueryResults.map(snap => snap.docs).flat();

        const carPromises = carDocs.map(async doc => {
          const data = doc.data() || {};
          const inferredCollection = doc.ref?.parent?.id || '';
          const collectionToTypeMap = { deals: 'Buyer', cars: 'Consignor', leads: 'Lead' };
          const inferredType = collectionToTypeMap[inferredCollection] || inferredCollection;
          const carSnap = await db.doc('cars/' + (data.stock || doc.id)).get();
          const car = carSnap.data() || {};

          const rowLink = undefined;
          const rowAction = inferredType === 'Lead'
            ? (() => StateManager.openLeadModal(doc.id))
            : (() => StateManager.openCarModal(carSnap.id));

          const carTitle = `${car.year || ''}${car.year ? ' ' : ''}${car.make || ''}${car.make ? ' ' : ''}${car.model || ''}${car.model ? ' ' : ''}${car.trim === 'Base' ? '' : (car.trim || '')}`.trim();
          return {
            id: doc.id,
            ...data,
            ...car,
            type: inferredType,
            car: carTitle,
            thumbnail_cell: (
              <Box
                component="img"
                src={car.thumbnail || '/missing_image.jpeg'}
                alt={carTitle}
                onError={(e) => { e.target.src = '/missing_image.jpeg'; }}
                sx={{
                  width: 64,
                  height: 40,
                  objectFit: 'cover',
                  borderRadius: 0.5,
                  display: 'block',
                  bgcolor: 'grey.100',
                }}
              />
            ),
            market_price: car?.pricing?.excellent || 'N/A',
            rowLink,
            rowAction,
          };
        });

        const carRows = await Promise.all(carPromises);
        if (cancelled) return;
        setCarData({
          rows: carRows,
          headers: [
            { key: 'stock', label: 'Stock Number' },
            { key: 'thumbnail_cell', label: '', noLink: true },
            { key: 'car', label: 'Car' },
            { key: 'type', label: 'Type' },
            { key: 'price', label: 'Listing Price', format: 'usd' },
            { key: 'status', label: 'Status' },
            { key: 'location', label: 'Location' },
            { key: 'date', label: 'Arrival' },
            { key: 'status_time', label: 'Last Update' },
          ],
          title: 'Customer Vehicles',
        });

        const serviceSnap = await db.collection('orders').where('customer', '==', id).get();
        if (cancelled) return;
        let orders = serviceSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const servicePromises = orders.map(order =>
          db.collection('services').where('order', '==', order.id).get()
        );
        const servicesSnaps = await Promise.all(servicePromises);
        if (cancelled) return;
        const services = servicesSnaps.map(servicesSnap =>
          servicesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        ).flat();

        // Fetch car docs for each order so we can show thumbnail + year/make/model/trim
        const carStockSet = new Set(orders.map(o => o.car).filter(Boolean));
        const carDocSnaps = await Promise.all(
          [...carStockSet].map(stock => db.doc('cars/' + stock).get())
        );
        if (cancelled) return;
        const carByStock = {};
        carDocSnaps.forEach(snap => {
          if (snap.exists) carByStock[snap.id] = snap.data();
        });

        orders.forEach((order, i) => {
          const filteredServices = services.filter(x => x.order === order.id);
          orders[i].services = filteredServices;
          orders[i].cost = filteredServices.reduce((a, c) => a + (c.cost || 0), 0);

          const carDoc = order.car ? carByStock[order.car] : null;
          const carTitle = carDoc
            ? [carDoc.year, carDoc.make, carDoc.model, carDoc.trim === 'Base' ? '' : carDoc.trim]
                .filter(Boolean).join(' ')
            : '';
          orders[i].car_title = order.car
            ? (carTitle ? `${order.car} · ${carTitle}` : order.car)
            : carTitle;
          orders[i].thumbnail_cell = (
            <Box
              component="img"
              src={carDoc?.thumbnail || '/missing_image.jpeg'}
              alt={carTitle}
              onError={(e) => { e.target.src = '/missing_image.jpeg'; }}
              sx={{
                width: 64,
                height: 40,
                objectFit: 'cover',
                borderRadius: 0.5,
                display: 'block',
                bgcolor: 'grey.100',
              }}
            />
          );

          orders[i].rowAction = (event) => {
            if (event?.ctrlKey || event?.metaKey) {
              window.open(`${window.location.pathname}?so=${order.id}`, '_blank');
              return;
            }
            StateManager.openServiceOrderModal(order.id);
          };
        });

        const summary = [
          { label: 'Orders', value: orders.length },
          { label: 'Revenue', value: orders.reduce((a, c) => a + (c.revenue || 0), 0), format: 'usd' },
        ];

        setServiceData({
          rows: orders,
          summary,
          headers: [
            { key: 'stock', label: 'SO Number' },
            { key: 'date', label: 'Date' },
            { key: 'thumbnail_cell', label: '', noLink: true },
            { key: 'car_title', label: 'Car' },
            { key: 'status', label: 'Status' },
            { key: 'revenue', label: 'Revenue', format: 'usd' },
          ],
          title: 'Service Orders',
        });
      } catch (error) {
        console.error('Error fetching customer data:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchAllData();

    return () => { cancelled = true; };
  }, [id, refreshTrigger]);

  const handleDataRefresh = () => setRefreshTrigger(prev => prev + 1);

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
    const tabKey = VISIBLE_TABS[newValue];
    const url = new URL(window.location.href);
    url.searchParams.set('customerTab', tabKey);
    history.replace(url.pathname + '?' + url.searchParams.toString());
  };

  const handleClose = () => {
    if (onClose) onClose();
  };

  const handlePrint = () => {
    const node = tabContentRef.current;
    if (!node) return;
    const tabLabel = formatTitle(VISIBLE_TABS[activeTab] || '');
    const printWindow = window.open('', '', 'height=900,width=1100');
    if (!printWindow) return;
    printWindow.document.write(`<!doctype html><html><head><title>${displayName} — ${tabLabel}</title>
      <style>
        body { font-family: 'Helvetica Neue', Arial, sans-serif; margin: 24px; color: #1a1a1a; }
        h1 { font-size: 16pt; margin: 0 0 4px 0; color: #023e84; }
        .ctp-meta { font-size: 9pt; color: #555; margin-bottom: 16px; }
        table { width: 100%; border-collapse: collapse; font-size: 9pt; margin-bottom: 12px; }
        thead th { background: #023e84; color: #fff; text-align: left; padding: 6px 8px; font-weight: 600; font-size: 8.5pt; text-transform: uppercase; letter-spacing: 0.4px; }
        tbody td { padding: 6px 8px; border-bottom: 1px solid #e8ecf0; vertical-align: middle; }
        tbody tr:nth-child(even) td { background: #fafbfc; }
        td a { color: inherit; text-decoration: none; }
        img { max-width: 80px; max-height: 50px; object-fit: cover; border-radius: 2px; display: block; }
        button, input[type=text], textarea, select, .MuiFab-root, .no-print { display: none !important; }
        .MuiPaper-root { box-shadow: none !important; border: none !important; padding: 0 !important; margin: 0 0 12px 0 !important; }
        .MuiTypography-h6 { font-size: 11pt; font-weight: 700; color: #023e84; margin: 0 0 6px 0; }
        @page { margin: 0.6in; }
      </style></head><body>
      <h1>${displayName}</h1>
      <div class="ctp-meta">${tabLabel} · printed ${new Date().toLocaleString()}</div>
      ${node.innerHTML}
      </body></html>`);
    printWindow.document.close();
    printWindow.focus();
    // Wait a beat so images can decode before the print dialog opens
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 300);
  };

  const handleMergeComplete = (primaryId) => {
    setMergeDialogOpen(false);
    StateManager.setAlertAndOpen('Customers merged successfully', 'success');
    if (primaryId && primaryId !== id) {
      StateManager.openCustomerModal(primaryId);
    } else {
      handleDataRefresh();
    }
  };

  const displayName = `${customerData.first_name || ''} ${customerData.last_name || ''}`.trim() || 'Customer';

  const tabContents = {
    summary: () => (
      <CustomerSummary
        cust_id={id}
        customerData={customerData}
        profileImageUrl={profileImageUrl}
        setProfileImageUrl={setProfileImageUrl}
      />
    ),
    cars: () => (
      <CarList
        cust_id={id}
        carData={carData}
        customerData={customerData}
        onDataRefresh={handleDataRefresh}
      />
    ),
    service: () => (
      <ServiceList
        cust_id={id}
        serviceData={serviceData}
        customerName={displayName}
        onDataRefresh={handleDataRefresh}
      />
    ),
    'credit-apps': () => (
      <CreditAppsTab
        cust_id={id}
        customerData={customerData}
      />
    ),
    'payment-methods': () => (
      <SavedCardsList
        customer={customerData}
        mode="manage"
        title="Cards on file"
      />
    ),
    files: () => (
      <EntityFiles
        type="customer"
        id={id}
        label={displayName}
      />
    ),
  };

  const activeTabKey = VISIBLE_TABS[activeTab];

  return (
    <ModalStackProvider value={myZ}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        fullWidth
        fullScreen={fullScreen}
        sx={{ zIndex: myZ }}
        PaperProps={{
          sx: {
            ...paperSx,
            overflow: 'hidden',
          },
        }}
      >
        {/* ── Top bar ── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            {id ? displayName : 'Loading…'}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton onClick={handleClose} size="small" sx={{ color: theme.palette.text.secondary }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* ── Tab bar ── */}
        <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, flexShrink: 0, px: 1 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 40,
              '& .MuiTab-root': { minHeight: 40, py: 0, fontSize: '0.8rem', textTransform: 'none', fontWeight: 500 },
              '& .Mui-selected': { fontWeight: 700 },
            }}
          >
            {VISIBLE_TABS.map(key => (
              <Tab key={key} label={formatTitle(key)} />
            ))}
          </Tabs>
        </Box>

        {/* ── Scrollable content ── */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-track': { background: theme.palette.grey[100] },
            '&::-webkit-scrollbar-thumb': { background: theme.palette.grey[300], borderRadius: 3 },
          }}
        >
          {/* ── Profile header ── */}
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1.5, sm: 2.5 },
              px: { xs: 2, sm: 3 },
              py: { xs: 1.5, sm: 2 },
              bgcolor: alpha(theme.palette.grey[50], 0.8),
              borderBottom: `1px solid ${theme.palette.divider}`,
              flexWrap: 'wrap',
            }}
          >
            <Avatar
              src={profileImageUrl}
              alt={displayName}
              sx={{
                width: { xs: 72, sm: 96 },
                height: { xs: 72, sm: 96 },
                boxShadow: theme.shadows[2],
                bgcolor: theme.palette.grey[200],
              }}
            >
              <PersonIcon sx={{ fontSize: 40, color: theme.palette.grey[500] }} />
            </Avatar>

            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2, mb: 0.5, fontSize: '1rem' }} noWrap>
                {displayName || '—'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                {customerData.phone_number && (
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                    {customerData.phone_number}
                  </Typography>
                )}
                {customerData.email && (
                  <Typography variant="body2" sx={{ color: theme.palette.text.disabled, fontSize: '0.75rem' }}>
                    {customerData.email}
                  </Typography>
                )}
                {customerData.city && customerData.state && (
                  <Typography variant="body2" sx={{ color: theme.palette.text.disabled, fontSize: '0.75rem' }}>
                    {customerData.city}, {customerData.state}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              <Tooltip title={`Print current tab (${formatTitle(activeTabKey || '')})`}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    onClick={handlePrint}
                    disabled={!id || loading}
                  >
                    Print
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Merge with duplicate customer(s)">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<MergeTypeIcon />}
                    onClick={() => setMergeDialogOpen(true)}
                    disabled={!id || loading}
                  >
                    Merge
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>

          {/* Tab content */}
          <Box ref={tabContentRef} sx={{ p: 2 }}>
            {loading ? <Loading /> : (tabContents[activeTabKey] ? tabContents[activeTabKey]() : <Loading />)}
          </Box>
        </Box>
      </Dialog>

      <MergeCustomersDialog
        open={mergeDialogOpen}
        primaryId={id}
        primaryCustomer={customerData}
        onClose={() => setMergeDialogOpen(false)}
        onComplete={handleMergeComplete}
      />
    </ModalStackProvider>
  );
}
